import React, { useState, useEffect } from "react";
import { getMembersAdmin, createMember, updateMember, deleteMember, uploadImage } from "../../../APIClient/apiClient";
import { resolveImageSrc } from "../../../utils/resolveImageSrc";
import Toast from "../../../component/Toast";
import "../../../style/admin/AnnouncementManager.css";

const MembersManager = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState("en");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    en: { name: "", role: "", organization: "", research: "" },
    vi: { name: "", role: "", organization: "", research: "" },
    image_url: "",
    email: "",
    website_url: "",
    sort_order: 0,
    is_active: true,
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const data = await getMembersAdmin();
      setMembers(data);
    } catch (err) {
      setToast({ message: "Failed to load members", type: "error" });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (language, field, value) => {
    setFormData({
      ...formData,
      [language]: {
        ...formData[language],
        [field]: value,
      },
    });
  };

  const handleMetaChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleImageFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setToast({ message: "Please select an image file", type: "error" });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setToast({ message: "Image size must be less than 5MB", type: "error" });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    setUploadingImage(true);
    try {
      const result = await uploadImage(file);
      setFormData({
        ...formData,
        image_url: result.imageUrl,
      });
      setToast({ message: "Image uploaded successfully", type: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to upload image", type: "error" });
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        translations: {
          en: formData.en,
          vi: formData.vi,
        },
        image_url: formData.image_url,
        email: formData.email,
        website_url: formData.website_url,
        sort_order: formData.sort_order,
        is_active: formData.is_active,
      };

      if (editingId) {
        await updateMember(editingId, payload);
      } else {
        await createMember(payload);
      }
      fetchMembers();
      resetForm();
      setToast({ message: "Member saved successfully", type: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to save member", type: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this member?")) {
      try {
        await deleteMember(id);
        fetchMembers();
        setToast({ message: "Member deleted successfully", type: "success" });
      } catch (err) {
        setToast({ message: "Failed to delete member", type: "error" });
      }
    }
  };

  const handleEdit = (member) => {
    setFormData({
      en: {
        name: member.translations.en?.name || "",
        role: member.translations.en?.role || "",
        organization: member.translations.en?.organization || "",
        research: member.translations.en?.research || "",
      },
      vi: {
        name: member.translations.vi?.name || "",
        role: member.translations.vi?.role || "",
        organization: member.translations.vi?.organization || "",
        research: member.translations.vi?.research || "",
      },
      image_url: member.image_url || "",
      email: member.email || "",
      website_url: member.website_url || "",
      sort_order: member.sort_order,
      is_active: member.is_active,
    });
    setImagePreview(member.image_url || null);
    setEditingId(member.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      en: { name: "", role: "", organization: "", research: "" },
      vi: { name: "", role: "", organization: "", research: "" },
      image_url: "",
      email: "",
      website_url: "",
      sort_order: 0,
      is_active: true,
    });
    setEditingId(null);
    setShowForm(false);
    setActiveTab("en");
    setImagePreview(null);
  };

  if (loading) return <div className="admin-manager-loading">Loading members...</div>;

  return (
    <div className="admin-manager">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />

      <div className="admin-manager-header">
        <button className="admin-manager-btn-primary" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
          {showForm ? "Cancel" : "Add New Member"}
        </button>
      </div>

      {showForm && (
        <div className="admin-manager-form">
          <h3>{editingId ? "Edit Member" : "Create New Member"}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-tabs">
              <button type="button" className={activeTab === "en" ? "active" : ""} onClick={() => setActiveTab("en")}>
                English
              </button>
              <button type="button" className={activeTab === "vi" ? "active" : ""} onClick={() => setActiveTab("vi")}>
                Vietnamese
              </button>
            </div>

            {activeTab === "en" && (
              <div className="form-section">
                <div className="form-group">
                  <label>Name (EN) *</label>
                  <input
                    type="text"
                    value={formData.en.name}
                    onChange={(e) => handleInputChange("en", "name", e.target.value)}
                    placeholder="English name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Role (EN)</label>
                  <input
                    type="text"
                    value={formData.en.role}
                    onChange={(e) => handleInputChange("en", "role", e.target.value)}
                    placeholder="e.g., Professor, Researcher"
                  />
                </div>
                <div className="form-group">
                  <label>Organization (EN)</label>
                  <input
                    type="text"
                    value={formData.en.organization}
                    onChange={(e) => handleInputChange("en", "organization", e.target.value)}
                    placeholder="Organization name"
                  />
                </div>
                <div className="form-group">
                  <label>Research (EN)</label>
                  <textarea
                    value={formData.en.research}
                    onChange={(e) => handleInputChange("en", "research", e.target.value)}
                    placeholder="Research interests"
                    rows="4"
                  />
                </div>
              </div>
            )}

            {activeTab === "vi" && (
              <div className="form-section">
                <div className="form-group">
                  <label>Name (VI) *</label>
                  <input
                    type="text"
                    value={formData.vi.name}
                    onChange={(e) => handleInputChange("vi", "name", e.target.value)}
                    placeholder="Vietnamese name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Role (VI)</label>
                  <input
                    type="text"
                    value={formData.vi.role}
                    onChange={(e) => handleInputChange("vi", "role", e.target.value)}
                    placeholder="e.g., Giáo sư, Nhà nghiên cứu"
                  />
                </div>
                <div className="form-group">
                  <label>Organization (VI)</label>
                  <input
                    type="text"
                    value={formData.vi.organization}
                    onChange={(e) => handleInputChange("vi", "organization", e.target.value)}
                    placeholder="Tên tổ chức"
                  />
                </div>
                <div className="form-group">
                  <label>Research (VI)</label>
                  <textarea
                    value={formData.vi.research}
                    onChange={(e) => handleInputChange("vi", "research", e.target.value)}
                    placeholder="Lĩnh vực nghiên cứu"
                    rows="4"
                  />
                </div>
              </div>
            )}

            <div className="form-section">
              <h4>General Information</h4>
              <div className="form-group">
                <label>Image URL</label>
                <input type="file" accept="image/*" onChange={handleImageFileChange} disabled={uploadingImage} />
                {uploadingImage && <p style={{ color: "blue", marginTop: "5px" }}>Uploading image...</p>}
                {imagePreview && (
                  <div style={{ marginTop: "10px" }}>
                    <img
                      src={resolveImageSrc(imagePreview)}
                      alt="Member preview"
                      style={{ maxWidth: "200px", maxHeight: "160px", objectFit: "cover", borderRadius: "8px" }}
                    />
                  </div>
                )}
                <input
                  type="text"
                  value={formData.image_url}
                  onChange={(e) => handleMetaChange("image_url", e.target.value)}
                  placeholder="https://... or /images/members/..."
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleMetaChange("email", e.target.value)}
                  placeholder="email@example.com"
                />
              </div>
              <div className="form-group">
                <label>Website URL</label>
                <input
                  type="text"
                  value={formData.website_url}
                  onChange={(e) => handleMetaChange("website_url", e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Sort Order</label>
                <input type="number" value={formData.sort_order} onChange={(e) => handleMetaChange("sort_order", parseInt(e.target.value))} />
              </div>
              <div className="form-group">
                <label>Active</label>
                <input type="checkbox" checked={formData.is_active} onChange={(e) => handleMetaChange("is_active", e.target.checked)} />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? "Update" : "Create"} Member
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-manager-table">
        <table>
          <thead>
            <tr>
              <th>No.</th>
              <th>Name (EN)</th>
              <th>Role</th>
              <th>Organization</th>
              <th>Email</th>
              <th>Active</th>
              <th>Sort Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>
                  No members found
                </td>
              </tr>
            ) : (
              members.map((member, index) => (
                <tr key={member.id}>
                  <td>{index + 1}</td>
                  <td>{member.translations.en?.name || "N/A"}</td>
                  <td>{member.translations.en?.role || "N/A"}</td>
                  <td>{member.translations.en?.organization || "N/A"}</td>
                  <td>{member.email || "N/A"}</td>
                  <td>{member.is_active ? "Yes" : "No"}</td>
                  <td>{member.sort_order}</td>
                  <td className="action-buttons">
                    <button className="btn-edit" onClick={() => handleEdit(member)}>
                      Edit
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(member.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MembersManager;
